import type {
  AgentRuntimeErrorItem,
  AgentRuntimeTimelineItem,
  AgentRuntimeToolCallItem,
  AgentRuntimeUsageState,
  AgentRuntimeUsageTotals,
} from "@cord/shared"
import { normalizeAgentErrorMessage } from "@cord/shared"
import {
  asRecord,
  asString,
  buildAttachment,
  buildUsageTotalsFromOpenCodeTokens,
  extractActorMetadata,
  isTaskToolPart,
  messageHasOpenRenderableAssistantPart,
  normalizeTimestamp,
  normalizeToolStatus,
  OPENCODE_DERIVED_STATE_VERSION,
  resolveOpenCodeMessageOrderingTimestamp,
  resolveTimestamp,
  type ChildSessionToolPartsBySessionId,
  type MessageEntry,
  type OpenCodeChildSessionRuntimeState,
  type OpenCodeBatchMutationState,
  type OpenCodeDerivedState,
  type OpenCodeProjectionGraph,
  type OpenCodeTaskLinkIndexEntry,
  type TaskChildLink,
} from "@/lib/opencode-runtime-shared"
import {
  extractHiddenMessageLinkage,
} from "@/lib/session-context-transport"
import { extractRuntimeClientMessageId } from "@/lib/runtime-client-message-id"
import { resolveToolDisplayOutput } from "@/lib/tool-display-output"

type OpenCodeProjectionWithDerived = OpenCodeProjectionGraph & OpenCodeDerivedState & {
  providerStatus?: Record<string, unknown> | null
  pendingPermissionsById?: Record<string, Record<string, unknown>>
  pendingQuestionsById?: Record<string, Record<string, unknown>>
}

function isDismissedQuestionTool(args: {
  toolName: string | null
  status: AgentRuntimeToolCallItem["status"]
  error: string | null
}): boolean {
  if (args.status !== "error") return false
  const normalizedToolName = args.toolName?.trim().toLowerCase() ?? ""
  if (
    normalizedToolName !== "question"
    && normalizedToolName !== "askquestion"
    && normalizedToolName !== "askuserquestion"
  ) {
    return false
  }

  const normalizedError = args.error?.trim().toLowerCase() ?? ""
  if (!normalizedError) return false
  return normalizedError.includes("dismissed this question")
    || normalizedError.includes("questionrejectederror")
}

function buildToolMetadataWithChildSessionState(args: {
  metadata: Record<string, unknown> | undefined
  title: string | undefined
  childSessionId: string | undefined
  childSessionStateBySessionId: Record<string, OpenCodeChildSessionRuntimeState> | undefined
}): Record<string, unknown> | undefined {
  const childSessionId = args.childSessionId?.trim()
  const title = args.title?.trim()
  const baseMetadata = title
    ? { ...(args.metadata ?? {}), title }
    : args.metadata

  if (!childSessionId) return baseMetadata
  const childState = args.childSessionStateBySessionId?.[childSessionId]
  if (!childState) return baseMetadata

  return {
    ...(baseMetadata ?? {}),
    childSessionState: {
      statusType: childState.statusType ?? null,
      lastEventAt: childState.lastEventAt ?? null,
      finishReason: childState.finishReason ?? null,
      errorMessage: childState.errorMessage ?? null,
      errorType: childState.errorType ?? null,
    },
  }
}

function hasTerminalOpenCodeProviderStatus(args: {
  providerStatus: Record<string, unknown> | null
}): boolean {
  const runtimeFinality = asRecord(args.providerStatus?.runtimeFinality)
  const runtimeFinalityState = asString(runtimeFinality?.state)?.toLowerCase() ?? null
  const providerStatusType = asString(args.providerStatus?.type)?.toLowerCase() ?? null
  const finishedAt = normalizeTimestamp(args.providerStatus?.finishedAt)

  if (
    providerStatusType === "error" ||
    providerStatusType === "failed" ||
    providerStatusType === "completed"
  ) {
    return true
  }

  if (runtimeFinalityState === "settled") {
    return true
  }

  if (finishedAt === null) {
    return false
  }

  return (
    providerStatusType === "idle" ||
    providerStatusType === "error" ||
    providerStatusType === "failed" ||
    providerStatusType === "completed"
  )
}

function isTaskToolTerminalErrorReason(reason: string | null): boolean {
  return reason === "aborted" || reason === "failed" || reason === "error"
}

function resolveProviderTerminalToolStatus(args: {
  providerStatus: Record<string, unknown> | null
}): AgentRuntimeToolCallItem["status"] | null {
  if (!hasTerminalOpenCodeProviderStatus({ providerStatus: args.providerStatus })) {
    return null
  }

  const providerStatusType = asString(args.providerStatus?.type)?.toLowerCase() ?? null
  const finishReason = asString(args.providerStatus?.finishReason)?.toLowerCase() ?? null
  const runtimeFinality = asRecord(args.providerStatus?.runtimeFinality)
  const runtimeFinalityReason = asString(runtimeFinality?.reason)?.toLowerCase() ?? null

  if (
    providerStatusType === "error" ||
    providerStatusType === "failed" ||
    isTaskToolTerminalErrorReason(finishReason) ||
    isTaskToolTerminalErrorReason(runtimeFinalityReason)
  ) {
    return "error"
  }

  return "completed"
}

function resolveParentSessionTerminalToolStatus(args: {
  parentSessionStatusType?: string | null
  parentSessionFinishReason?: string | null
}): AgentRuntimeToolCallItem["status"] | null {
  const parentSessionStatusType = args.parentSessionStatusType?.trim().toLowerCase() ?? null
  const parentSessionFinishReason = args.parentSessionFinishReason?.trim().toLowerCase() ?? null

  if (parentSessionStatusType === "idle" || parentSessionStatusType === "completed") {
    return isTaskToolTerminalErrorReason(parentSessionFinishReason) ? "error" : "completed"
  }

  if (parentSessionStatusType === "error" || parentSessionStatusType === "failed") {
    return "error"
  }

  return null
}

function resolveParentSessionTerminalNonTaskToolStatus(args: {
  parentSessionStatusType?: string | null
  parentSessionFinishReason?: string | null
}): AgentRuntimeToolCallItem["status"] | null {
  const parentSessionStatusType = args.parentSessionStatusType?.trim().toLowerCase() ?? null
  const parentSessionFinishReason = args.parentSessionFinishReason?.trim().toLowerCase() ?? null

  if (parentSessionStatusType === "completed") {
    return isTaskToolTerminalErrorReason(parentSessionFinishReason) ? "error" : "completed"
  }

  if (parentSessionStatusType === "idle" && parentSessionFinishReason !== null) {
    return isTaskToolTerminalErrorReason(parentSessionFinishReason) ? "error" : "completed"
  }

  if (parentSessionStatusType === "error" || parentSessionStatusType === "failed") {
    return "error"
  }

  return null
}

function resolveCanonicalTaskToolStatus(args: {
  childSessionId: string | undefined
  childSessionStateBySessionId: Record<string, OpenCodeChildSessionRuntimeState> | undefined
  parentSessionFinishReason?: string | null
  parentSessionStatusType?: string | null
  providerStatus: Record<string, unknown> | null
  status: AgentRuntimeToolCallItem["status"]
  toolName: string
}): AgentRuntimeToolCallItem["status"] {
  if (args.toolName !== "task") {
    return args.status
  }

  if (args.status !== "pending" && args.status !== "running") {
    return args.status
  }

  const normalizedChildSessionId = args.childSessionId?.trim() ?? ""
  const childState = normalizedChildSessionId.length > 0
    ? args.childSessionStateBySessionId?.[normalizedChildSessionId] ?? null
    : null
  const childStatusType = childState?.statusType?.trim().toLowerCase() ?? null
  const childFinishReason = childState?.finishReason?.trim().toLowerCase() ?? null

  if (childStatusType === "idle" || childStatusType === "completed") {
    return isTaskToolTerminalErrorReason(childFinishReason) ? "error" : "completed"
  }

  if (childStatusType === "error" || childStatusType === "failed") {
    return "error"
  }

  const parentTerminalStatus = resolveParentSessionTerminalToolStatus({
    parentSessionStatusType: args.parentSessionStatusType,
    parentSessionFinishReason: args.parentSessionFinishReason,
  })
  if (parentTerminalStatus) return parentTerminalStatus

  return resolveProviderTerminalToolStatus({ providerStatus: args.providerStatus }) ?? args.status
}

function resolveCanonicalOpenCodeToolStatus(args: {
  childSessionId: string | undefined
  childSessionStateBySessionId: Record<string, OpenCodeChildSessionRuntimeState> | undefined
  isLatestAssistantMessage?: boolean
  messageCompletedAt?: number | null
  parentSessionFinishReason?: string | null
  parentSessionStatusType?: string | null
  providerStatus: Record<string, unknown> | null
  status: AgentRuntimeToolCallItem["status"]
  toolName: string
}): AgentRuntimeToolCallItem["status"] {
  const taskAwareStatus = resolveCanonicalTaskToolStatus(args)
  if (taskAwareStatus !== "pending" && taskAwareStatus !== "running") {
    return taskAwareStatus
  }

  if (args.toolName === "task") {
    return taskAwareStatus
  }

  const parentTerminalStatus = resolveParentSessionTerminalNonTaskToolStatus({
    parentSessionStatusType: args.parentSessionStatusType,
    parentSessionFinishReason: args.parentSessionFinishReason,
  })
  if (parentTerminalStatus) return parentTerminalStatus

  const providerTerminalStatus = resolveProviderTerminalToolStatus({
    providerStatus: args.providerStatus,
  })
  if (providerTerminalStatus) return providerTerminalStatus

  const isCompletedAssistantMessage =
    typeof args.messageCompletedAt === "number" && Number.isFinite(args.messageCompletedAt)
  const isStaleOlderAssistantMessage = args.isLatestAssistantMessage === false
  if (isCompletedAssistantMessage || isStaleOlderAssistantMessage) {
    return "completed"
  }

  return taskAwareStatus
}

function isHiddenUserTextPart(part: MessageEntry["partsById"][string]): boolean {
  if (part.type !== "text") return false
  if (part.synthetic === true) return true
  return asString(asRecord(part.metadata)?.kind) === "agent_mode_switch"
}

function sortMessageIdsByCreatedTime(messagesById: Record<string, MessageEntry>): string[] {
  return Object.entries(messagesById)
    .map(([messageId, message], index) => ({
      messageId,
      index,
      createdAt: resolveOpenCodeMessageOrderingTimestamp({ message }),
    }))
    .sort((left, right) => {
      if (left.createdAt !== null || right.createdAt !== null) {
        if (left.createdAt === null) return 1
        if (right.createdAt === null) return -1
        if (left.createdAt !== right.createdAt) return left.createdAt - right.createdAt
      }
      return left.index - right.index
    })
    .map((entry) => entry.messageId)
}

function getLatestAssistantMessageId(args: {
  orderedMessageIds: string[]
  messagesById: Record<string, MessageEntry>
}): string | null {
  for (let index = args.orderedMessageIds.length - 1; index >= 0; index -= 1) {
    const messageId = args.orderedMessageIds[index]
    if (!messageId) continue
    const message = args.messagesById[messageId]
    if (!message) continue
    const info = asRecord(message.info)
    if (info?.role === "user") continue
    return messageId
  }

  return null
}

function sortMessageParts(
  partsById: MessageEntry["partsById"],
  messageTimestamp: number,
): Array<Record<string, unknown>> {
  return Object.values(partsById)
    .map((part, index) => {
      const partTime = asRecord(part.time)
      const stateTime = asRecord(asRecord(part.state)?.time)
      return {
        part,
        index,
        timestamp: resolveTimestamp(
          partTime?.start,
          stateTime?.start,
          partTime?.end,
          stateTime?.end,
          messageTimestamp,
        ),
      }
    })
    .sort((left, right) => {
      if (left.timestamp !== right.timestamp) return left.timestamp - right.timestamp
      return left.index - right.index
    })
    .map((entry) => entry.part)
}

function sortChildSessionToolParts(
  partsById: Record<string, Record<string, unknown>>,
): Array<Record<string, unknown>> {
  return Object.values(partsById)
    .map((part, index) => {
      const partTime = asRecord(part.time)
      const stateTime = asRecord(asRecord(part.state)?.time)
      return {
        part,
        index,
        timestamp: resolveTimestamp(
          stateTime?.start,
          partTime?.start,
          stateTime?.end,
          partTime?.end,
          index + 1,
        ),
      }
    })
    .sort((left, right) => {
      if (left.timestamp !== right.timestamp) return left.timestamp - right.timestamp
      return left.index - right.index
    })
    .map((entry) => entry.part)
}

export function shouldTreatOpenStreamingTurnAsIncomplete(args: {
  providerStatus: Record<string, unknown> | null
  pendingInteractionCount?: number
  hasOpenRenderableAssistantPart?: boolean
}): boolean {
  const providerStatusType = asString(args.providerStatus?.type)?.toLowerCase()
  const finishedAt = normalizeTimestamp(args.providerStatus?.finishedAt)
  const runtimeFinality = asRecord(args.providerStatus?.runtimeFinality)
  const runtimeFinalityState = asString(runtimeFinality?.state)
  const finishedAtSupportsTerminalClosure =
    finishedAt !== null && (
      providerStatusType === "idle" ||
      providerStatusType === "error" ||
      providerStatusType === "failed" ||
      providerStatusType === "completed"
    )
  const hasOpenRenderableAssistantPart = args.hasOpenRenderableAssistantPart === true
  const pendingInteractionCount =
    typeof args.pendingInteractionCount === "number" && Number.isFinite(args.pendingInteractionCount)
      ? Math.max(0, Math.floor(args.pendingInteractionCount))
      : 0

  if (pendingInteractionCount > 0) {
    return true
  }

  if (
    providerStatusType === "busy"
    || providerStatusType === "running"
    || providerStatusType === "retry"
    || providerStatusType === "working"
  ) {
    return false
  }

  if (
    providerStatusType === "idle"
    || providerStatusType === "waiting_input"
    || providerStatusType === "error"
    || providerStatusType === "failed"
    || providerStatusType === "completed"
    || finishedAtSupportsTerminalClosure
  ) {
    if (
      hasOpenRenderableAssistantPart
      && !finishedAtSupportsTerminalClosure
      && runtimeFinalityState !== "settled"
      && providerStatusType !== "error"
      && providerStatusType !== "failed"
      && providerStatusType !== "completed"
    ) {
      return false
    }
    return true
  }

  return runtimeFinalityState === "settled"
}

function buildMessageTimelineItems(
  messageId: string,
  message: MessageEntry,
  childSessionStateBySessionId: Record<string, OpenCodeChildSessionRuntimeState> | undefined,
  providerStatus: Record<string, unknown> | null,
  pendingInteractionCount: number,
  latestAssistantMessageId: string | null,
): AgentRuntimeTimelineItem[] {
  const info = asRecord(message.info) ?? {}
  const time = asRecord(info.time)
  const normalizedMessageId = asString(info.id) ?? messageId
  const messageTimestamp = resolveOpenCodeMessageOrderingTimestamp({ message }) ?? Date.now()
  const parts = sortMessageParts(message.partsById, messageTimestamp)
  const sessionId = asString(info.sessionID) ?? undefined
  const allowMessageStreaming = info.role !== "user" && messageId === latestAssistantMessageId
  const messageCompletedAt = normalizeTimestamp(time?.completed)
  const compactionMarkers: AgentRuntimeTimelineItem[] = []

  for (const [partIndex, part] of parts.entries()) {
    if (part.type !== "compaction") continue
    const partId = asString(part.id) ?? `${normalizedMessageId}:compaction:${partIndex}`
    const partTime = asRecord(part.time)
    compactionMarkers.push({
      id: `opencode:compaction:${partId}`,
      turnId: normalizedMessageId,
      kind: "marker",
      markerType: "compaction",
      label: "Conversation compacted",
      timestamp: resolveTimestamp(partTime?.start, messageTimestamp),
      providerRef: {
        agentType: "opencode",
        sessionId,
        messageId: normalizedMessageId,
      },
    })
  }

  if (info.role === "user") {
    const clientMessageId = extractRuntimeClientMessageId({ value: info })
    const visibleTextSegments: string[] = []
    const rawTextSegments: string[] = []
    const contextRefs = new Map<string, { referenceId?: string; sessionId: string; repositoryId?: string | null; title?: string | null }>()
    const attachments = []
    let actor: AgentRuntimeTimelineItem["actor"]
    let attachmentCount = 0

    for (const part of parts) {
      if (part.type === "text") {
        actor ??= extractActorMetadata(part.metadata)
        const partText = typeof part.text === "string" ? part.text : ""
        if (partText.length > 0) {
          rawTextSegments.push(partText)
        }
        if (isHiddenUserTextPart(part)) continue
        if (partText.length > 0) {
          visibleTextSegments.push(partText)
        }
        continue
      }

      if (part.type === "file") {
        actor ??= extractActorMetadata(part.metadata)
        attachmentCount += 1
        const url = asString(part.url)
        if (!url) continue
        attachments.push(
          buildAttachment({
            id: asString(part.id) ?? `${normalizedMessageId}:attachment:${attachmentCount}`,
            url,
            filename: asString(part.filename),
            mime: asString(part.mime),
          }),
        )
      }
    }

    const extractedContext = extractHiddenMessageLinkage(rawTextSegments.join("\n"))
    for (const ref of extractedContext.contextRefs) {
      contextRefs.set(ref.referenceId ?? ref.sessionId, ref)
    }

    const visibleMessageLinkage = extractHiddenMessageLinkage(visibleTextSegments.join("\n"))
    const text = visibleMessageLinkage.text.trimEnd()
    if (
      text.length === 0
      && attachmentCount === 0
      && contextRefs.size === 0
      && extractedContext.requestedSkillRefs.length === 0
      && extractedContext.preferredMcpServerRefs.length === 0
      && !extractedContext.attachedPullRequest
    ) {
      return compactionMarkers
    }

    return [...compactionMarkers, {
      id: `opencode:user:${normalizedMessageId}`,
      turnId: normalizedMessageId,
      kind: "user_message",
      clientMessageId: visibleMessageLinkage.clientMessageId ?? extractedContext.clientMessageId ?? clientMessageId,
      text,
      attachmentCount: attachmentCount > 0 ? attachmentCount : undefined,
      attachments: attachments.length > 0 ? attachments : undefined,
      contextRefs: contextRefs.size > 0 ? Array.from(contextRefs.values()) : undefined,
      requestedSkillRefs:
        extractedContext.requestedSkillRefs.length > 0
          ? extractedContext.requestedSkillRefs
          : undefined,
      preferredMcpServerRefs:
        extractedContext.preferredMcpServerRefs.length > 0
          ? extractedContext.preferredMcpServerRefs
          : undefined,
      attachedPullRequest: extractedContext.attachedPullRequest,
      timestamp: messageTimestamp,
      actor,
      providerRef: {
        agentType: "opencode",
        sessionId,
        messageId: normalizedMessageId,
      },
    }]
  }

  const timelineItems: AgentRuntimeTimelineItem[] = [...compactionMarkers]
  const forceCloseOpenStreamingParts = shouldTreatOpenStreamingTurnAsIncomplete({
    providerStatus,
    pendingInteractionCount,
    hasOpenRenderableAssistantPart: messageHasOpenRenderableAssistantPart({
      partsById: message.partsById,
    }),
  })

  for (const [partIndex, part] of parts.entries()) {
    const partId = asString(part.id) ?? `${normalizedMessageId}:${partIndex}`
    const partTime = asRecord(part.time)
    const isTrailingPart = partIndex === parts.length - 1

    if (part.type === "compaction") {
      continue
    }

    if (part.type === "text") {
      const text = typeof part.text === "string" ? part.text : ""
      const hasVisibleText = text.trim().length > 0
      const isStreamingByRuntime =
        allowMessageStreaming &&
        isTrailingPart &&
        normalizeTimestamp(partTime?.end) === null &&
        normalizeTimestamp(time?.completed) === null
      const isStreaming = isStreamingByRuntime && !forceCloseOpenStreamingParts
      if (!hasVisibleText && !isStreaming) continue
      timelineItems.push({
        id: `opencode:assistant:${partId}`,
        turnId: normalizedMessageId,
        kind: "assistant_text",
        messageId: normalizedMessageId,
        text,
        streaming: isStreaming,
        timestamp: resolveTimestamp(partTime?.start, messageTimestamp),
        actor: extractActorMetadata(part.metadata),
        providerRef: {
          agentType: "opencode",
          sessionId,
          messageId: normalizedMessageId,
        },
      })
      continue
    }

    if (part.type === "reasoning") {
      const text = typeof part.text === "string" ? part.text : ""
      const hasVisibleText = text.trim().length > 0
      const isStreamingByRuntime =
        allowMessageStreaming &&
        isTrailingPart &&
        normalizeTimestamp(partTime?.end) === null &&
        normalizeTimestamp(time?.completed) === null
      const isStreaming = isStreamingByRuntime && !forceCloseOpenStreamingParts
      if (!hasVisibleText && !isStreaming) continue
      timelineItems.push({
        id: `opencode:thinking:${partId}`,
        turnId: normalizedMessageId,
        kind: "thinking",
        messageId: normalizedMessageId,
        text,
        streaming: isStreaming,
        timestamp: resolveTimestamp(partTime?.start, messageTimestamp),
        providerRef: {
          agentType: "opencode",
          sessionId,
          messageId: normalizedMessageId,
        },
      })
      continue
    }

    if (part.type !== "tool") continue
    const state = asRecord(part.state) ?? {}
    const stateTime = asRecord(state.time)
    const metadata = asRecord(state.metadata) ?? undefined
    const toolCallId = asString(part.callID) ?? partId
    const parentToolCallId =
      asString(metadata?.parentToolCallId) ??
      asString(metadata?.parentToolUseId) ??
      undefined
    const childSessionId = part.tool === "task"
      ? asString(metadata?.sessionId) ?? undefined
      : undefined
    const normalizedToolName = asString(part.tool) ?? "tool"
    const toolMetadata = buildToolMetadataWithChildSessionState({
      metadata,
      title: asString(state.title) ?? undefined,
      childSessionId,
      childSessionStateBySessionId,
    })
    const normalizedToolStatus = normalizeToolStatus(asString(state.status))
    const normalizedToolError = normalizeAgentErrorMessage({
      value: state.error,
      fallbackMessage: normalizedToolStatus === "error" ? "Tool failed" : null,
    })
    const dismissedQuestionTool = isDismissedQuestionTool({
      toolName: normalizedToolName,
      status: normalizedToolStatus,
      error: normalizedToolError,
    })
    const canonicalToolMetadata = dismissedQuestionTool
      ? { ...(toolMetadata ?? {}), interactiveOutcome: "dismissed" }
      : toolMetadata
    const canonicalToolStatus = dismissedQuestionTool
      ? "completed"
      : resolveCanonicalOpenCodeToolStatus({
          childSessionId,
          childSessionStateBySessionId,
          isLatestAssistantMessage: messageId === latestAssistantMessageId,
          messageCompletedAt,
          parentSessionFinishReason: null,
          parentSessionStatusType: null,
          providerStatus,
          status: normalizedToolStatus,
          toolName: normalizedToolName,
        })
    const childToolError = childSessionId
      ? childSessionStateBySessionId?.[childSessionId]?.errorMessage ?? null
      : null
    const canonicalToolError = dismissedQuestionTool
      ? null
      : normalizedToolError ?? (canonicalToolStatus === "error" ? childToolError : null)
    timelineItems.push({
      id: `opencode:tool:${toolCallId}`,
      turnId: normalizedMessageId,
      kind: "tool_call",
      messageId: normalizedMessageId,
      toolCallId,
      toolName: normalizedToolName,
      input: state.input,
      output: resolveToolDisplayOutput({
        toolName: normalizedToolName,
        output: state.output,
        metadata,
        status: canonicalToolStatus,
      }),
      error: canonicalToolError,
      status: canonicalToolStatus,
      timestamp: resolveTimestamp(stateTime?.start, partTime?.start, messageTimestamp),
      parentToolCallId,
      childSessionId,
      metadata: canonicalToolMetadata,
      providerRef: {
        agentType: "opencode",
        sessionId,
        messageId: normalizedMessageId,
        toolCallId,
        parentToolCallId,
        childSessionId,
      },
    })
  }

  return timelineItems
}

function registerTaskChildLink(
  taskLinksByChildSession: Map<string, TaskChildLink[]>,
  childSessionId: string,
  parentToolCallId: string,
  startedAt: number,
): void {
  const normalizedChildSessionId = childSessionId.trim()
  const normalizedParentToolCallId = parentToolCallId.trim()
  if (!normalizedChildSessionId || !normalizedParentToolCallId) return
  const current = taskLinksByChildSession.get(normalizedChildSessionId) ?? []
  current.push({ parentToolCallId: normalizedParentToolCallId, startedAt })
  current.sort((left, right) => left.startedAt - right.startedAt)
  taskLinksByChildSession.set(normalizedChildSessionId, current)
}

function collectTaskLinkFromToolPart(
  taskLinksByChildSession: Map<string, TaskChildLink[]>,
  part: Record<string, unknown>,
  fallbackTimestamp: number,
): void {
  if (!isTaskToolPart(part)) return
  const toolCallId = asString(part.callID) ?? asString(part.id)
  const state = asRecord(part.state) ?? {}
  const stateTime = asRecord(state.time)
  const metadata = asRecord(state.metadata)
  const childSessionId = asString(metadata?.sessionId)
  if (!toolCallId || !childSessionId) return
  registerTaskChildLink(
    taskLinksByChildSession,
    childSessionId,
    toolCallId,
    resolveTimestamp(stateTime?.start, fallbackTimestamp),
  )
}

function collectTaskChildLinks(
  messagesById: Record<string, MessageEntry>,
  childSessionToolPartsBySessionId: ChildSessionToolPartsBySessionId,
): Map<string, TaskChildLink[]> {
  const taskLinksByChildSession = new Map<string, TaskChildLink[]>()

  for (const message of Object.values(messagesById)) {
    const messageTimestamp = resolveOpenCodeMessageOrderingTimestamp({ message }) ?? Date.now()
    for (const part of Object.values(message.partsById)) {
      collectTaskLinkFromToolPart(taskLinksByChildSession, part, messageTimestamp)
    }
  }

  for (const partsById of Object.values(childSessionToolPartsBySessionId)) {
    for (const part of Object.values(partsById)) {
      collectTaskLinkFromToolPart(
        taskLinksByChildSession,
        part,
        resolveTimestamp(asRecord(asRecord(part.state)?.time)?.start),
      )
    }
  }

  return taskLinksByChildSession
}

function buildTaskLinkIndexByChildSessionId(args: {
  messagesById: Record<string, MessageEntry>
  childSessionToolPartsBySessionId: ChildSessionToolPartsBySessionId
}): Record<string, OpenCodeTaskLinkIndexEntry> {
  const nextIndex: Record<string, OpenCodeTaskLinkIndexEntry> = {}

  const ensureEntry = (childSessionId: string): OpenCodeTaskLinkIndexEntry => {
    if (!nextIndex[childSessionId]) {
      nextIndex[childSessionId] = {
        messageIds: [],
        parentChildSessionIds: [],
      }
    }

    return nextIndex[childSessionId]
  }

  for (const [messageId, message] of Object.entries(args.messagesById)) {
    for (const part of Object.values(message.partsById)) {
      if (!isTaskToolPart(part)) continue
      const state = asRecord(part.state)
      const metadata = asRecord(state?.metadata)
      const childSessionId = asString(metadata?.sessionId)
      if (!childSessionId) continue
      const entry = ensureEntry(childSessionId)
      if (!entry.messageIds.includes(messageId)) {
        entry.messageIds.push(messageId)
      }
    }
  }

  for (const [parentChildSessionId, partsById] of Object.entries(args.childSessionToolPartsBySessionId)) {
    for (const part of Object.values(partsById)) {
      if (!isTaskToolPart(part)) continue
      const state = asRecord(part.state)
      const metadata = asRecord(state?.metadata)
      const childSessionId = asString(metadata?.sessionId)
      if (!childSessionId) continue
      const entry = ensureEntry(childSessionId)
      if (!entry.parentChildSessionIds.includes(parentChildSessionId)) {
        entry.parentChildSessionIds.push(parentChildSessionId)
      }
    }
  }

  return nextIndex
}

function resolveTaskParentToolCallId(
  taskLinksByChildSession: Map<string, TaskChildLink[]>,
  childSessionId: string,
  timestamp: number,
): string | undefined {
  const links = taskLinksByChildSession.get(childSessionId)
  if (!links || links.length === 0) return undefined
  // Child-session tool parts can arrive with incomplete or skewed timestamps
  // during streaming. Default to the newest known parent task so live activity
  // remains attached to the active subagent even before clocks fully align.
  let fallback = links[links.length - 1]?.parentToolCallId
  for (const link of links) {
    if (link.startedAt <= timestamp) {
      fallback = link.parentToolCallId
      continue
    }
    break
  }
  return fallback
}

function buildChildSessionTimelineItems(
  sessionId: string,
  partsById: Record<string, Record<string, unknown>>,
  taskLinksByChildSession: Map<string, TaskChildLink[]>,
  childSessionStateBySessionId: Record<string, OpenCodeChildSessionRuntimeState> | undefined,
): AgentRuntimeToolCallItem[] {
  const parts = sortChildSessionToolParts(partsById)
  const timelineItems: AgentRuntimeToolCallItem[] = []

  for (const [partIndex, part] of parts.entries()) {
    if (part.type !== "tool") continue

    const partId = asString(part.id) ?? `${sessionId}:${partIndex}`
    const state = asRecord(part.state) ?? {}
    const stateTime = asRecord(state.time)
    const metadata = asRecord(state.metadata) ?? undefined
    const toolCallId = asString(part.callID) ?? partId
    const timestamp = resolveTimestamp(stateTime?.start, stateTime?.end)
    const parentToolCallId =
      asString(metadata?.parentToolCallId) ??
      asString(metadata?.parentToolUseId) ??
      resolveTaskParentToolCallId(taskLinksByChildSession, sessionId, timestamp)
    const childSessionId = part.tool === "task"
      ? asString(metadata?.sessionId) ?? undefined
      : undefined
    const messageId = asString(part.messageID) ?? undefined
    const normalizedToolName = asString(part.tool) ?? "tool"
    const toolMetadata = buildToolMetadataWithChildSessionState({
      metadata,
      title: asString(state.title) ?? undefined,
      childSessionId,
      childSessionStateBySessionId,
    })
    const normalizedToolStatus = normalizeToolStatus(asString(state.status))
    const normalizedToolError = normalizeAgentErrorMessage({
      value: state.error,
      fallbackMessage: normalizedToolStatus === "error" ? "Tool failed" : null,
    })
    const dismissedQuestionTool = isDismissedQuestionTool({
      toolName: normalizedToolName,
      status: normalizedToolStatus,
      error: normalizedToolError,
    })
    const canonicalToolMetadata = dismissedQuestionTool
      ? { ...(toolMetadata ?? {}), interactiveOutcome: "dismissed" }
      : toolMetadata
    const canonicalToolStatus = dismissedQuestionTool
      ? "completed"
      : resolveCanonicalOpenCodeToolStatus({
          childSessionId,
          childSessionStateBySessionId,
          isLatestAssistantMessage: undefined,
          messageCompletedAt: null,
          parentSessionFinishReason:
            childSessionStateBySessionId?.[sessionId]?.finishReason ?? null,
          parentSessionStatusType:
            childSessionStateBySessionId?.[sessionId]?.statusType ?? null,
          providerStatus: null,
          status: normalizedToolStatus,
          toolName: normalizedToolName,
        })
    const childToolError = childSessionId
      ? childSessionStateBySessionId?.[childSessionId]?.errorMessage ?? null
      : null
    const canonicalToolError = dismissedQuestionTool
      ? null
      : normalizedToolError ?? (canonicalToolStatus === "error" ? childToolError : null)

    timelineItems.push({
      id: `opencode:child-tool:${sessionId}:${toolCallId}`,
      turnId: messageId ?? `opencode:child-turn:${sessionId}`,
      kind: "tool_call",
      messageId,
      toolCallId,
      toolName: normalizedToolName,
      input: state.input,
      output: resolveToolDisplayOutput({
        toolName: normalizedToolName,
        output: state.output,
        metadata,
        status: canonicalToolStatus,
      }),
      error: canonicalToolError,
      status: canonicalToolStatus,
      timestamp,
      parentToolCallId,
      childSessionId,
      metadata: canonicalToolMetadata,
      providerRef: {
        agentType: "opencode",
        sessionId,
        messageId,
        toolCallId,
        parentToolCallId,
        childSessionId,
      },
    })
  }

  return timelineItems
}

function mergeSessionErrorsIntoOrderedTimeline(args: {
  orderedItems: AgentRuntimeTimelineItem[]
  sessionErrors: AgentRuntimeErrorItem[]
}): AgentRuntimeTimelineItem[] {
  if (args.sessionErrors.length === 0) return args.orderedItems

  const sortedSessionErrors = [...args.sessionErrors].sort((left, right) => {
    if (left.timestamp !== right.timestamp) return left.timestamp - right.timestamp
    return left.id.localeCompare(right.id)
  })

  const merged: AgentRuntimeTimelineItem[] = []
  let errorIndex = 0

  for (const item of args.orderedItems) {
    while (
      errorIndex < sortedSessionErrors.length &&
      sortedSessionErrors[errorIndex].timestamp <= item.timestamp
    ) {
      merged.push(sortedSessionErrors[errorIndex])
      errorIndex += 1
    }
    merged.push(item)
  }

  while (errorIndex < sortedSessionErrors.length) {
    merged.push(sortedSessionErrors[errorIndex])
    errorIndex += 1
  }

  return merged
}

function flattenOpenCodeTimeline(projection: OpenCodeProjectionWithDerived): AgentRuntimeTimelineItem[] {
  const totalLength =
    projection.orderedMessageIds.reduce(
      (sum, messageId) => sum + (projection.messageTimelineItemsById[messageId]?.length ?? 0),
      0,
    ) + projection.orderedChildSessionIds.reduce(
      (sum, sessionId) => sum + (projection.childSessionTimelineItemsBySessionId[sessionId]?.length ?? 0),
      0,
    ) + (projection.sessionErrors?.length ?? 0)

  if (totalLength === 0) return []

  const leadingItems: AgentRuntimeTimelineItem[] = []
  leadingItems.length = totalLength - projection.orderedChildSessionIds.reduce(
    (sum, sessionId) => sum + (projection.childSessionTimelineItemsBySessionId[sessionId]?.length ?? 0),
    0,
  )
  let offset = 0

  for (const messageId of projection.orderedMessageIds) {
    const items = projection.messageTimelineItemsById[messageId]
    if (!items || items.length === 0) continue
    for (const item of items) {
      leadingItems[offset] = item
      offset += 1
    }
  }

  const mergedLeadingItems = mergeSessionErrorsIntoOrderedTimeline({
    orderedItems: leadingItems.slice(0, offset),
    sessionErrors: projection.sessionErrors ?? [],
  })

  const timeline = new Array<AgentRuntimeTimelineItem>(totalLength)
  offset = 0
  for (const item of mergedLeadingItems) {
    timeline[offset] = item
    offset += 1
  }

  for (const sessionId of projection.orderedChildSessionIds) {
    const items = projection.childSessionTimelineItemsBySessionId[sessionId]
    if (!items || items.length === 0) continue
    for (const item of items) {
      timeline[offset] = item
      offset += 1
    }
  }

  return timeline
}

function tryRefreshOpenCodeTimelineForSingleTailMessage(args: {
  projection: OpenCodeProjectionWithDerived
  mutation: OpenCodeBatchMutationState
  previousMessageTimelineItemsById: Record<string, AgentRuntimeTimelineItem[]> | null
}): boolean {
  if (!args.previousMessageTimelineItemsById) return false
  if (args.mutation.dirtyMessageIds.size !== 1) return false
  if (
    args.mutation.timelineDirty ||
    args.mutation.dirtyChildSessionIds.size > 0 ||
    args.mutation.messageOrderDirty ||
    args.mutation.childSessionOrderDirty ||
    args.mutation.taskLinksDirty
  ) {
    return false
  }
  // The fast path only covers the common streaming case: the final top-level
  // assistant message is growing and no child-session tail or session error can
  // interleave after it. Mixed timelines still take the full flatten path below.
  if (args.projection.orderedChildSessionIds.length > 0) return false
  if ((args.projection.sessionErrors?.length ?? 0) > 0) return false

  const dirtyMessageId = args.mutation.dirtyMessageIds.values().next().value
  if (!dirtyMessageId) return false
  if (args.projection.orderedMessageIds.at(-1) !== dirtyMessageId) return false

  const previousDirtyItems = args.previousMessageTimelineItemsById[dirtyMessageId] ?? []
  const nextDirtyItems = args.projection.messageTimelineItemsById[dirtyMessageId] ?? []
  const previousTimeline = args.projection.timeline
  if (previousDirtyItems.length === 0) return false
  if (previousTimeline.length < previousDirtyItems.length) return false

  const prefixLength = previousTimeline.length - previousDirtyItems.length
  for (let index = 0; index < previousDirtyItems.length; index += 1) {
    if (previousTimeline[prefixLength + index]?.id !== previousDirtyItems[index]?.id) {
      return false
    }
  }

  args.projection.timeline = [
    ...previousTimeline.slice(0, prefixLength),
    ...nextDirtyItems,
  ]
  return true
}

function buildMessageUsageTotals(message: MessageEntry): AgentRuntimeUsageTotals | null {
  const info = asRecord(message.info)
  if (!info || info.role !== "assistant") return null

  const messageUsage = buildUsageTotalsFromOpenCodeTokens(info.tokens)
  if (messageUsage) return messageUsage

  const parts = Object.values(message.partsById)
  for (let partIndex = parts.length - 1; partIndex >= 0; partIndex -= 1) {
    const part = parts[partIndex]
    if (part.type !== "step-finish") continue
    const partUsage = buildUsageTotalsFromOpenCodeTokens(part.tokens)
    if (partUsage) return partUsage
  }

  return null
}

function buildUsageStateFromOrderedMessages(
  orderedMessageIds: string[],
  usageByMessageId: Record<string, AgentRuntimeUsageTotals | null>,
): AgentRuntimeUsageState {
  for (let index = orderedMessageIds.length - 1; index >= 0; index -= 1) {
    const messageId = orderedMessageIds[index]
    if (!messageId) continue
    const usage = usageByMessageId[messageId]
    if (!usage) continue
    return {
      latestTurn: usage,
      currentContext: usage,
    }
  }

  return {}
}

export function rebuildOpenCodeProjectionDerivedState<TProjection extends OpenCodeProjectionWithDerived>(
  projection: TProjection,
): TProjection {
  const orderedMessageIds = sortMessageIdsByCreatedTime(projection.messagesById)
  const latestAssistantMessageId = getLatestAssistantMessageId({
    orderedMessageIds,
    messagesById: projection.messagesById,
  })
  const messageTimelineItemsById: Record<string, AgentRuntimeTimelineItem[]> = {}
  const usageByMessageId: Record<string, AgentRuntimeUsageTotals | null> = {}
  const pendingInteractionCount =
    Object.keys(projection.pendingPermissionsById ?? {}).length
    + Object.keys(projection.pendingQuestionsById ?? {}).length

  for (const messageId of orderedMessageIds) {
    const message = projection.messagesById[messageId]
    if (!message) continue
    messageTimelineItemsById[messageId] = buildMessageTimelineItems(
      messageId,
      message,
      projection.childSessionStateBySessionId,
      projection.providerStatus ?? null,
      pendingInteractionCount,
      latestAssistantMessageId,
    )
    usageByMessageId[messageId] = buildMessageUsageTotals(message)
  }

  const orderedChildSessionIds = Object.keys(projection.childSessionToolPartsBySessionId)
    .sort((left, right) => left.localeCompare(right))
  const taskLinksByChildSession = collectTaskChildLinks(
    projection.messagesById,
    projection.childSessionToolPartsBySessionId,
  )
  const childSessionTimelineItemsBySessionId: Record<string, AgentRuntimeToolCallItem[]> = {}
  for (const sessionId of orderedChildSessionIds) {
    childSessionTimelineItemsBySessionId[sessionId] = buildChildSessionTimelineItems(
      sessionId,
      projection.childSessionToolPartsBySessionId[sessionId] ?? {},
      taskLinksByChildSession,
      projection.childSessionStateBySessionId,
    )
  }

  const nextProjection = {
    ...projection,
    derivedStateVersion: OPENCODE_DERIVED_STATE_VERSION,
    taskLinkIndexByChildSessionId: buildTaskLinkIndexByChildSessionId({
      messagesById: projection.messagesById,
      childSessionToolPartsBySessionId: projection.childSessionToolPartsBySessionId,
    }),
    taskLinkIndexValid: true,
    orderedMessageIds,
    messageTimelineItemsById,
    orderedChildSessionIds,
    childSessionTimelineItemsBySessionId,
    timeline: [],
    usageByMessageId,
    usage: {},
  } as TProjection

  nextProjection.timeline = flattenOpenCodeTimeline(nextProjection)
  nextProjection.usage = buildUsageStateFromOrderedMessages(orderedMessageIds, usageByMessageId)
  return nextProjection
}

export function hasOpenCodeProjectionDerivedState(projection: OpenCodeProjectionWithDerived): boolean {
  return (
    projection.derivedStateVersion === OPENCODE_DERIVED_STATE_VERSION &&
    Array.isArray(projection.orderedMessageIds) &&
    Array.isArray(projection.orderedChildSessionIds) &&
    Array.isArray(projection.timeline) &&
    typeof projection.messageTimelineItemsById === "object" &&
    projection.messageTimelineItemsById !== null &&
    typeof projection.childSessionTimelineItemsBySessionId === "object" &&
    projection.childSessionTimelineItemsBySessionId !== null &&
    typeof projection.usageByMessageId === "object" &&
    projection.usageByMessageId !== null &&
    typeof projection.usage === "object" &&
    projection.usage !== null
  )
}

export function ensureOpenCodeProjectionDerivedState<TProjection extends OpenCodeProjectionWithDerived>(
  projection: TProjection,
): TProjection {
  return hasOpenCodeProjectionDerivedState(projection)
    ? projection
    : rebuildOpenCodeProjectionDerivedState(projection)
}

export function refreshOpenCodeProjectionDerivedState<TProjection extends OpenCodeProjectionWithDerived>(
  projection: TProjection,
  mutation: OpenCodeBatchMutationState,
): void {
  if (
    mutation.dirtyMessageIds.size === 0 &&
    mutation.dirtyChildSessionIds.size === 0 &&
    !mutation.messageOrderDirty &&
    !mutation.childSessionOrderDirty &&
    !mutation.timelineDirty &&
    !mutation.taskLinksDirty &&
    !mutation.usageDirty
  ) {
    return
  }

  const previousLatestAssistantMessageId = getLatestAssistantMessageId({
    orderedMessageIds: projection.orderedMessageIds,
    messagesById: projection.messagesById,
  })

  if (mutation.messageOrderDirty) {
    projection.orderedMessageIds = sortMessageIdsByCreatedTime(projection.messagesById)
  }

  const latestAssistantMessageId = getLatestAssistantMessageId({
    orderedMessageIds: projection.orderedMessageIds,
    messagesById: projection.messagesById,
  })

  if (previousLatestAssistantMessageId !== latestAssistantMessageId) {
    if (previousLatestAssistantMessageId) {
      mutation.dirtyMessageIds.add(previousLatestAssistantMessageId)
    }
    if (latestAssistantMessageId) {
      mutation.dirtyMessageIds.add(latestAssistantMessageId)
    }
  }

  let previousMessageTimelineItemsById: Record<string, AgentRuntimeTimelineItem[]> | null = null

  if (mutation.dirtyMessageIds.size > 0) {
    previousMessageTimelineItemsById = projection.messageTimelineItemsById
    const nextMessageTimelineItemsById = { ...projection.messageTimelineItemsById }
    const nextUsageByMessageId = { ...projection.usageByMessageId }
    const pendingInteractionCount =
      Object.keys(projection.pendingPermissionsById ?? {}).length
      + Object.keys(projection.pendingQuestionsById ?? {}).length

    for (const messageId of mutation.dirtyMessageIds) {
      const message = projection.messagesById[messageId]
      if (!message) {
        delete nextMessageTimelineItemsById[messageId]
        delete nextUsageByMessageId[messageId]
        continue
      }

      nextMessageTimelineItemsById[messageId] = buildMessageTimelineItems(
        messageId,
        message,
        projection.childSessionStateBySessionId,
        projection.providerStatus ?? null,
        pendingInteractionCount,
        latestAssistantMessageId,
      )
      nextUsageByMessageId[messageId] = buildMessageUsageTotals(message)
    }

    projection.messageTimelineItemsById = nextMessageTimelineItemsById
    projection.usageByMessageId = nextUsageByMessageId
  }

  if (mutation.childSessionOrderDirty) {
    projection.orderedChildSessionIds = Object.keys(projection.childSessionToolPartsBySessionId)
      .sort((left, right) => left.localeCompare(right))
  }

  if (mutation.taskLinksDirty) {
    projection.taskLinkIndexByChildSessionId = buildTaskLinkIndexByChildSessionId({
      messagesById: projection.messagesById,
      childSessionToolPartsBySessionId: projection.childSessionToolPartsBySessionId,
    })
    projection.taskLinkIndexValid = true
  }

  if (mutation.taskLinksDirty || mutation.dirtyChildSessionIds.size > 0) {
    const taskLinksByChildSession = collectTaskChildLinks(
      projection.messagesById,
      projection.childSessionToolPartsBySessionId,
    )
    const nextChildSessionTimelineItemsBySessionId = {
      ...projection.childSessionTimelineItemsBySessionId,
    }
    const sessionsToRefresh = new Set<string>(mutation.dirtyChildSessionIds)
    if (mutation.taskLinksDirty) {
      for (const sessionId of projection.orderedChildSessionIds) {
        sessionsToRefresh.add(sessionId)
      }
    }

    for (const sessionId of sessionsToRefresh) {
      const partsById = projection.childSessionToolPartsBySessionId[sessionId]
      if (!partsById) {
        delete nextChildSessionTimelineItemsBySessionId[sessionId]
        continue
      }

      nextChildSessionTimelineItemsBySessionId[sessionId] = buildChildSessionTimelineItems(
        sessionId,
        partsById,
        taskLinksByChildSession,
        projection.childSessionStateBySessionId,
      )
    }

    projection.childSessionTimelineItemsBySessionId = nextChildSessionTimelineItemsBySessionId
  }

  if (
    mutation.timelineDirty ||
    mutation.dirtyMessageIds.size > 0 ||
    mutation.dirtyChildSessionIds.size > 0 ||
    mutation.messageOrderDirty ||
    mutation.childSessionOrderDirty ||
    mutation.taskLinksDirty
  ) {
    const usedTailMessageFastPath = tryRefreshOpenCodeTimelineForSingleTailMessage({
      projection,
      mutation,
      previousMessageTimelineItemsById,
    })
    if (!usedTailMessageFastPath) {
      projection.timeline = flattenOpenCodeTimeline(projection)
    }
  }

  if (mutation.usageDirty || mutation.messageOrderDirty) {
    projection.usage = buildUsageStateFromOrderedMessages(
      projection.orderedMessageIds,
      projection.usageByMessageId,
    )
  }
}
